import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Box, Link } from '@mui/material';

interface MarkdownContentProps {
  content: string;
}

export const MarkdownContent: React.FC<MarkdownContentProps> = ({ content }) => {
  // Unescape literal \n characters that may come from JSON
  const processedContent = content.replace(/\\n/g, '\n');

  return (
    <Box
      sx={{
        // Prose styling - clean, readable typography
        fontFamily: '"Inter", system-ui, sans-serif',
        fontSize: '0.875rem',
        lineHeight: 1.6,
        color: 'text.primary',
        '& p': {
          margin: '0 0 0.75em 0',
          '&:last-child': { marginBottom: 0 },
        },
        '& h1': {
          fontSize: '1.25em',
          fontWeight: 600,
          marginBottom: '0.5em',
          marginTop: '1em',
          '&:first-of-type': { marginTop: 0 },
        },
        '& h2': {
          fontSize: '1.1em',
          fontWeight: 600,
          marginBottom: '0.5em',
          marginTop: '1em',
        },
        '& h3': {
          fontSize: '1em',
          fontWeight: 600,
          marginBottom: '0.5em',
          marginTop: '0.75em',
        },
        '& code': {
          fontFamily: '"JetBrains Mono", monospace',
          fontSize: '0.85em',
          backgroundColor: 'action.hover',
          padding: '0.15em 0.35em',
          borderRadius: '4px',
        },
        '& pre': {
          backgroundColor: 'action.selected',
          padding: '0.8em',
          borderRadius: '8px',
          overflow: 'auto',
          marginBottom: '0.8em',
          '& code': {
            backgroundColor: 'transparent',
            padding: 0,
            fontSize: '0.85em',
          },
        },
        '& ul': {
          listStyleType: 'disc',
          paddingLeft: '1.2em',
          marginBottom: '0.5em',
        },
        '& ol': {
          listStyleType: 'decimal',
          paddingLeft: '1.2em',
          marginBottom: '0.5em',
        },
        '& li': {
          marginBottom: '0.25em',
        },
        '& blockquote': {
          borderLeft: 3,
          borderColor: 'divider',
          paddingLeft: '1em',
          marginLeft: 0,
          color: 'text.secondary',
          fontStyle: 'italic',
        },
        '& table': {
          borderCollapse: 'collapse',
          width: '100%',
          marginBottom: '1em',
        },
        '& th, & td': {
          border: 1,
          borderColor: 'divider',
          padding: '0.5em 0.75em',
          textAlign: 'left',
        },
        '& th': {
          backgroundColor: 'action.hover',
          fontWeight: 600,
        },
        '& hr': {
          border: 'none',
          borderTop: 1,
          borderColor: 'divider',
          margin: '1em 0',
        },
        '& strong': {
          fontWeight: 600,
        },
        '& a': {
          color: 'primary.main',
          textDecoration: 'none',
          '&:hover': {
            textDecoration: 'underline',
          },
        },
      }}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: ({ href, children }) => (
            <Link href={href} target="_blank" rel="noopener noreferrer">
              {children}
            </Link>
          ),
        }}
      >
        {processedContent}
      </ReactMarkdown>
    </Box>
  );
};
